import React, { useState, useEffect } from 'react';
import { FiSearch, FiX, FiGlobe, FiUserCheck, FiHelpCircle } from 'react-icons/fi';

const DEV_GROUPS = [
  {
    title: 'INTRODUCCIÓN',
    items: [
      { id: 'overview', label: 'Visión General' },
      { id: 'authentication', label: 'Autenticación & Keys' },
      { id: 'prep-time', label: 'Tiempo de Preparación' },
      { id: 'errors', label: 'Códigos de Error' }
    ]
  },
  {
    title: 'GUÍAS DE INSTALACIÓN',
    items: [
      { id: 'install-woocommerce', label: 'WordPress / WooCommerce' },
      { id: 'install-shopify', label: 'Shopify Integration' },
      { id: 'install-odoo', label: 'Odoo ERP Addon' },
      { id: 'install-custom', label: 'Integración Personalizada' }
    ]
  },
  {
    title: 'SDKS & OPENAPI',
    items: [
      { id: 'openapi-spec', label: 'OpenAPI 3.1 Spec' },
      { id: 'sdk-javascript', label: 'JS/TS Monorepo (@veez)' }
    ]
  },
  {
    title: 'COTIZACIONES & ENVÍOS',
    items: [
      { id: 'post-shipping-quote', label: 'Cotizar Envío', method: 'POST' },
      { id: 'post-orders', label: 'Crear Pedido / Despacho', method: 'POST' },
      { id: 'get-orders-id', label: 'Estado del Pedido', method: 'GET' },
      { id: 'post-orders-cancel', label: 'Cancelar Pedido', method: 'POST' }
    ]
  },
  {
    title: 'COMERCIO & CONFIGURACIÓN',
    items: [
      { id: 'get-merchants-me', label: 'Perfil de Comercio', method: 'GET' },
      { id: 'post-merchants-settings', label: 'Ajustes del Comercio', method: 'PATCH' }
    ]
  },
  {
    title: 'PLATAFORMAS E-COMMERCE',
    items: [
      { id: 'woocommerce-endpoint', label: 'WooCommerce API', method: 'POST' },
      { id: 'shopify-endpoint', label: 'Shopify CarrierService', method: 'POST' },
      { id: 'odoo-endpoint', label: 'Odoo Connector', method: 'POST' }
    ]
  },
  {
    title: 'EVENTOS & WEBHOOKS',
    items: [
      { id: 'webhooks', label: 'Webhooks en Tiempo Real' }
    ]
  }
];

const MERCHANT_GROUPS = [
  {
    title: 'GUÍA DE COMERCIOS',
    items: [
      { id: 'merchant-overview', label: 'Visión General Comercios' },
      { id: 'merchant-prep-time', label: 'Tiempo de Preparación' },
      { id: 'merchant-shipping-rates', label: 'Tarifas & Cobertura' },
      { id: 'merchant-returns', label: 'Gestión de Devoluciones' },
      { id: 'merchant-faq', label: 'Preguntas Frecuentes' }
    ]
  }
];

const CLIENT_GROUPS = [
  {
    title: 'GUÍA DE CLIENTES & RASTREO',
    items: [
      { id: 'client-overview', label: 'Visión General Destinatarios' },
      { id: 'client-tracking', label: 'Rastreo GPS en Tiempo Real' },
      { id: 'client-delivery-verification', label: 'Confirmación de Entrega (PIN)' },
      { id: 'client-faq', label: 'Preguntas Frecuentes' }
    ]
  }
];

const DRIVER_GROUPS = [
  {
    title: 'GUÍA DE REPARTIDORES',
    items: [
      { id: 'driver-overview', label: 'Visión General Conductores' },
      { id: 'driver-eta', label: 'Tiempos de Llegada (ETAs)' },
      { id: 'driver-faq', label: 'Preguntas Frecuentes' }
    ]
  }
];

export default function Sidebar({ activeTab, activeSection, onSelectSection, mobileMenuOpen, onCloseMobileMenu }) {
  const [searchQuery, setSearchQuery] = useState('');

  let navGroups = DEV_GROUPS;
  if (activeTab === 'comercios') navGroups = MERCHANT_GROUPS;
  if (activeTab === 'clientes') navGroups = CLIENT_GROUPS;
  if (activeTab === 'repartidores') navGroups = DRIVER_GROUPS;

  // Auto-detect active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('.doc-section');
      let currentActive = activeSection;

      sections.forEach((sec) => {
        const rect = sec.getBoundingClientRect();
        if (rect.top <= 140 && rect.bottom >= 140) {
          currentActive = sec.id;
        }
      });

      if (currentActive && currentActive !== activeSection && onSelectSection) {
        onSelectSection(currentActive);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSection, onSelectSection]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({
        top: el.offsetTop - 80,
        behavior: 'smooth'
      });
      if (onSelectSection) onSelectSection(id);
      if (onCloseMobileMenu) onCloseMobileMenu();
    }
  };

  return (
    <>
      {/* OVERLAY PARA MOBILE */}
      {mobileMenuOpen && (
        <div className="sidebar-backdrop" onClick={onCloseMobileMenu} />
      )}

      <aside className={`left-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* Brand Header */}
        <div className="sidebar-brand-container">
          <div className="sidebar-brand-logo" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img 
              src="/assets/img/veez_logo_transparent.png" 
              alt="Veez Logo" 
              style={{ width: '24px', height: '24px', objectFit: 'contain' }} 
            />
            <span className="sidebar-brand-name" style={{ fontWeight: 600 }}>VeezDocs</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="sidebar-version-badge">v2.0</span>
            {mobileMenuOpen && (
              <button className="mobile-close-btn" onClick={onCloseMobileMenu} aria-label="Cerrar menú">
                <FiX size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Search Input */}
        <div className="sidebar-search-box">
          <FiSearch className="search-icon" size={14} />
          <input
            type="text"
            placeholder="Buscar tema o endpoint..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="sidebar-search-input"
          />
        </div>

        {/* Navigation Groups */}
        <nav className="sidebar-nav">
          {navGroups.map((group, idx) => {
            const filteredItems = group.items.filter(item =>
              item.label.toLowerCase().includes(searchQuery.toLowerCase())
            );
            if (searchQuery && filteredItems.length === 0) return null;

            return (
              <div key={idx} className="sidebar-group">
                <div className="sidebar-group-title">{group.title}</div>
                <div className="sidebar-group-items">
                  {filteredItems.map((item, itemIdx) => {
                    const isActive = activeSection === item.id;
                    return (
                      <button
                        key={itemIdx}
                        onClick={() => scrollToSection(item.id)}
                        className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                      >
                        <span className="sidebar-nav-label">{item.label}</span>
                        {item.method && (
                          <span className={`method-tag ${item.method.toLowerCase()}`}>
                            {item.method}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* PLATFORM LINKS EN MOBILE DRAWER */}
          <div className="sidebar-mobile-platform-links" style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
            <div className="sidebar-group-title">PLATAFORMA VEEZ</div>
            <div className="sidebar-group-items" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <a href="https://veez.cl" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', fontSize: '13px', color: '#475569', textDecoration: 'none', borderRadius: '6px' }}>
                <FiGlobe size={14} /> Sitio Oficial Veez.cl
              </a>
              <a href="https://app.veez.cl" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', fontSize: '13px', color: '#475569', textDecoration: 'none', borderRadius: '6px' }}>
                <FiUserCheck size={14} /> Panel Comercio
              </a>
              <a href="https://veez.cl/contacto" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', fontSize: '13px', color: '#475569', textDecoration: 'none', borderRadius: '6px' }}>
                <FiHelpCircle size={14} /> Soporte Técnico
              </a>
            </div>
          </div>
        </nav>
      </aside>
    </>
  );
}
